import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Input,
  Button,
  Space,
  message,
  Switch,
  Image,
  Card,
  Spin,
  Alert,
  Select,
  Table,
} from 'antd';
import type { UploadFile, } from 'antd';
import {
  ScanOutlined,
  UploadOutlined,
  CameraOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useIntl } from 'umi';
import Webcam from 'react-webcam';
import { request } from '@umijs/max';
import { getWarehouses, type Warehouse } from '@/services/ant-design-pro/warehouses';

const { TextArea } = Input;
const { Option } = Select;

interface WarehouseReceiptModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface OrderItem {
  itemUniqueNo?: number;
  subject: string;
  quantity: number;
  imageUrl: string;
  skuAttributes: Array<{
    attributeId: number;
    attributeName: string;
    value: string;
  }>;
  notes?: string;
  notesZh?: string;
  designatedShooting?: Array<{
    note?: string;
    noteZh?: string;
    photo?: string;
  }>;
}

interface SectionDataResponse {
  status: string;
  statusCode: number;
  message: string;
  data: {
    section: string;
    trackingNumber: string;
    exists: boolean;
    data: {
      receivedAt?: string;
      receivedBy?: string;
      // cashOnDelivery?: number;
    } | null;
    package?: {
      _id: string;
      trackingNumber: string;
      workflowStep: string;
      status: string;
      warehouse: {
        _id: string;
        name: string;
      };
      inspectionInfo?: {
        photos: string[];
        productPhotos: string[];
        issuePhotos: string[];
        waybillPhotos: string[];
        notes: string;
        hasIssue: boolean;
        inspectedAt: string;
        inspectedBy: string;
      };
    } | null;
    order: {
      _id?: string;
      orderNumber?: string;
      parentOrderNo?: string;
      items: OrderItem[];
    } | null;
    warehouse: any;
    message: string;
  };
  timestamp: string;
}

const WarehouseReceiptModal: React.FC<WarehouseReceiptModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const intl = useIntl();
  
  // State
  const [trackingNumber, setTrackingNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scanData, setScanData] = useState<SectionDataResponse['data'] | null>(null);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showRackDesignation, setShowRackDesignation] = useState(false);
  const [locationCode, setLocationCode] = useState('');
  
  // Image upload states
  const [waybillImages, setWaybillImages] = useState<UploadFile[]>([]);
  const [productImages, setProductImages] = useState<UploadFile[]>([]);
  const [issueImages, setIssueImages] = useState<UploadFile[]>([]);
  const [hasIssue, setHasIssue] = useState(false);
  const [notes, setNotes] = useState('');
  // const [cashOnDelivery, setCashOnDelivery] = useState<number>(0);
  
  // Warehouse
  const [warehouse, setWarehouse] = useState<string>('');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  
  // Camera
  const [showCamera, setShowCamera] = useState(false);
  const [cameraType, setCameraType] = useState<'waybill' | 'product' | 'issue' | null>(null);
  const [showUploadChoice, setShowUploadChoice] = useState(false);
  const [uploadChoiceType, setUploadChoiceType] = useState<'waybill' | 'product' | 'issue' | null>(null);
  const webcamRef = useRef<Webcam>(null);

  // Fetch warehouses on modal open
  useEffect(() => {
    if (visible) {
      fetchWarehouses();
    }
  }, [visible]);

  const fetchWarehouses = async () => {
    setLoadingWarehouses(true);
    try {
      const response = await getWarehouses({ isActive: true }, { skipErrorHandler: true });
      const warehousesList =
        response?.data?.warehouses ??
        (response as { warehouses?: Warehouse[] } | null)?.warehouses ??
        [];
      
      if (warehousesList.length > 0) {
        setWarehouses(warehousesList);
        if (!warehouse) {
          const defaultWarehouse = warehousesList.find((w: Warehouse) => w.isDefault) || warehousesList[0];
          setWarehouse(defaultWarehouse._id);
        }
      }
    } catch (error) {
      // console.error('Error fetching warehouses:', error);
      message.error(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.loadWarehousesFailed' }),
      );
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const handleScan = async () => {
    if (!trackingNumber.trim()) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.enterTrackingNumber' }),
      );
      return;
    }

    if (!warehouse) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.selectWarehouse' }),
      );
      return;
    }

    setLoading(true);
    try {
      const response = await request<SectionDataResponse>(
        `/pda/workflow/receipt/${trackingNumber.trim()}`,
        {
          method: 'GET',
        }
      );

      if (response.statusCode === 200 && response.data) {
        setScanData(response.data);
        // Load cashOnDelivery from section data if present (value is in CNY)
        // const sectionCash = response.data.data?.cashOnDelivery;
        // setCashOnDelivery(typeof sectionCash === 'number' && sectionCash >= 0 ? sectionCash : 0);
        // If package exists (reinspection case), load existing data
        if (response.data.exists && response.data.package?.inspectionInfo) {
          const inspection = response.data.package.inspectionInfo;
          setHasIssue(inspection.hasIssue);
          setNotes(inspection.notes || '');
          
          // Load existing images as URLs
          const existingWaybill = inspection.waybillPhotos.map((url, idx) => ({
            uid: `waybill-${idx}`,
            name: `waybill-${idx}.jpg`,
            status: 'done' as const,
            url,
          }));
          const existingProduct = inspection.productPhotos.map((url, idx) => ({
            uid: `product-${idx}`,
            name: `product-${idx}.jpg`,
            status: 'done' as const,
            url,
          }));
          const existingIssue = inspection.issuePhotos.map((url, idx) => ({
            uid: `issue-${idx}`,
            name: `issue-${idx}.jpg`,
            status: 'done' as const,
            url,
          }));
          
          setWaybillImages(existingWaybill);
          setProductImages(existingProduct);
          setIssueImages(existingIssue);
        }
      } else {
        message.error(
          response.message ||
            intl.formatMessage({ id: 'pages.orders.warehouseReceipt.scanFailed' }),
        );
      }
    } catch (error: any) {
      // console.error('Scan error:', error);
      message.error(
        error?.response?.data?.message ||
          intl.formatMessage({ id: 'pages.orders.warehouseReceipt.scanFailed' }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterNoData = () => {
    setShowImageUpload(true);
  };

  const handleRegister = () => {
    setShowImageUpload(true);
  };

  const handleReinspection = () => {
    setShowImageUpload(true);
  };

  const handleRackDesignation = () => {
    setShowRackDesignation(true);
  };

  const handleRackDesignationConfirm = async () => {
    if (!locationCode.trim()) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.enterLocationCode' }),
      );
      return;
    }

    if (!trackingNumber.trim()) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.enterTrackingNumber' }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const response = await request<{
        status: string;
        statusCode: number;
        message?: string;
        data?: {
          package: any;
          rack: any;
          order: any;
          isDuplicate: boolean;
          message: string;
        };
      }>('/pda/workflow/load', {
        method: 'POST',
        data: {
          trackingNumber: trackingNumber.trim(),
          locationCode: locationCode.trim(),
        },
      });

      if (response?.statusCode === 200 || response?.status === 'success') {
        message.success(
          response?.message ||
            intl.formatMessage({ id: 'pages.orders.warehouseReceipt.loadingComplete' }),
        );
        resetForm();
        onSuccess?.();
        onClose();
      } else {
        message.error(
          response?.message ||
            intl.formatMessage({ id: 'pages.orders.warehouseReceipt.loadingFailed' }),
        );
      }
    } catch (error: any) {
      // console.error('Error completing loading:', error);
      if (error?.response?.status === 409) {
        message.error(
          intl.formatMessage({ id: 'pages.orders.warehouseReceipt.rackOccupied' }),
        );
      } else {
        message.error(
          error?.response?.data?.message ||
            intl.formatMessage({ id: 'pages.orders.warehouseReceipt.loadingFailed' }),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRackDesignation = () => {
    setShowRackDesignation(false);
    setLocationCode('');
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleCancelAction = () => {
    setScanData(null);
    setShowImageUpload(false);
    setShowRackDesignation(false);
    setWaybillImages([]);
    setProductImages([]);
    setIssueImages([]);
    setHasIssue(false);
    setNotes('');
    setLocationCode('');
  };

  const resetForm = () => {
    setTrackingNumber('');
    setScanData(null);
    setShowImageUpload(false);
    setShowRackDesignation(false);
    setWaybillImages([]);
    setProductImages([]);
    setIssueImages([]);
    setHasIssue(false);
    setNotes('');
    // setCashOnDelivery(0);
    setLocationCode('');
  };

  const handleCapturePhoto = () => {
    if (!webcamRef.current) {
      message.error(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.cameraNotReady' }),
      );
      return;
    }

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      message.error(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.captureFailed' }),
      );
      return;
    }

    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], `${cameraType}_${Date.now()}.jpg`, { type: 'image/jpeg' });
        const uploadFile: UploadFile = {
          uid: `-${Date.now()}-${Math.random()}`,
          name: file.name,
          status: 'done',
          url: imageSrc,
          thumbUrl: imageSrc,
          originFileObj: file as any,
        };

        if (cameraType === 'waybill') {
          setWaybillImages([...waybillImages, uploadFile]);
        } else if (cameraType === 'product') {
          setProductImages([...productImages, uploadFile]);
        } else if (cameraType === 'issue') {
          setIssueImages([...issueImages, uploadFile]);
        }

        setShowCamera(false);
        setCameraType(null);
        message.success(
          intl.formatMessage({ id: 'pages.orders.warehouseReceipt.captureSuccess' }),
        );
      })
      .catch((error) => {
        // console.error('Error converting photo to file:', error);
        message.error(
          intl.formatMessage({ id: 'pages.orders.warehouseReceipt.captureFailed' }),
        );
      });
  };

  const handleShowUploadChoice = (type: 'waybill' | 'product' | 'issue') => {
    setUploadChoiceType(type);
    setShowUploadChoice(true);
  };

  const handleChoiceCamera = () => {
    if (uploadChoiceType) {
      setCameraType(uploadChoiceType);
      setShowCamera(true);
      setShowUploadChoice(false);
      setUploadChoiceType(null);
    }
  };

  const handleChoiceFile = () => {
    const currentType = uploadChoiceType;
    setShowUploadChoice(false);
    setUploadChoiceType(null);
    
    // Create a temporary file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const files = target.files;
      
      if (files) {
        const validFiles: File[] = [];
        
        Array.from(files).forEach((file) => {
          const isImage = file.type.startsWith('image/');
          if (!isImage) {
            message.error(
              intl.formatMessage({ id: 'pages.orders.warehouseReceipt.onlyImagesAllowed' }),
            );
            return;
          }
          const isLt10M = file.size / 1024 / 1024 < 10;
          if (!isLt10M) {
            message.error(
              intl.formatMessage({ id: 'pages.orders.warehouseReceipt.imageTooLarge' }),
            );
            return;
          }
          validFiles.push(file);
        });

        const newFiles: UploadFile[] = await Promise.all(
          validFiles.map(
            (file) =>
              new Promise<UploadFile>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const dataUrl = reader.result as string;
                  resolve({
                    uid: `-${Date.now()}-${Math.random()}`,
                    name: file.name,
                    status: 'done',
                    url: dataUrl,
                    thumbUrl: dataUrl,
                    originFileObj: file as any,
                  });
                };
                reader.readAsDataURL(file);
              }),
          ),
        );

        if (newFiles.length > 0) {
          if (currentType === 'waybill') {
            setWaybillImages((prev) => [...prev, ...newFiles].slice(0, 10));
          } else if (currentType === 'product') {
            setProductImages((prev) => [...prev, ...newFiles].slice(0, 10));
          } else if (currentType === 'issue') {
            setIssueImages((prev) => [...prev, ...newFiles].slice(0, 10));
          }
        }
      }
    };
    
    input.click();
  };

  const handleConfirm = async () => {
    if (!trackingNumber.trim()) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.enterTrackingNumber' }),
      );
      return;
    }

    if (!warehouse) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.selectWarehouse' }),
      );
      return;
    }

    if (waybillImages.length === 0) {
      message.warning(
        intl.formatMessage({ id: 'pages.orders.warehouseReceipt.noWaybillImage' }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('trackingNumber', trackingNumber.trim());
      formData.append('warehouseId', warehouse);
      formData.append('hasIssue', String(hasIssue));
      formData.append('notes', notes);
      // formData.append('cashOnDelivery', String(Number(cashOnDelivery) || 0));

      // Separate new files and existing URLs
      const existingWaybillUrls: string[] = [];
      const existingProductUrls: string[] = [];
      const existingIssueUrls: string[] = [];

      // Waybill photos
      waybillImages.forEach((file) => {
        if (file.originFileObj) {
          formData.append('waybillPhotos', file.originFileObj);
        } else if (file.url && !file.url.startsWith('blob:') && !file.url.startsWith('data:')) {
          existingWaybillUrls.push(file.url);
        }
      });

      // Product photos
      productImages.forEach((file) => {
        if (file.originFileObj) {
          formData.append('productPhotos', file.originFileObj);
        } else if (file.url && !file.url.startsWith('blob:') && !file.url.startsWith('data:')) {
          existingProductUrls.push(file.url);
        }
      });

      // Issue photos
      issueImages.forEach((file) => {
        if (file.originFileObj) {
          formData.append('issuePhotos', file.originFileObj);
        } else if (file.url && !file.url.startsWith('blob:') && !file.url.startsWith('data:')) {
          existingIssueUrls.push(file.url);
        }
      });

      // Add existing URLs as JSON arrays
      if (existingWaybillUrls.length > 0) {
        formData.append('existingWaybillPhotoUrls', JSON.stringify(existingWaybillUrls));
      }
      if (existingProductUrls.length > 0) {
        formData.append('existingProductPhotoUrls', JSON.stringify(existingProductUrls));
      }
      if (existingIssueUrls.length > 0) {
        formData.append('existingIssuePhotoUrls', JSON.stringify(existingIssueUrls));
      }

      const response = await request<{
        status: string;
        statusCode: number;
        message?: string;
      }>('/pda/workflow/receipt', {
        method: 'POST',
        data: formData,
        requestType: 'form',
      });

      if (response?.statusCode === 200 || response?.status === 'success') {
        message.success(
          response?.message ||
            intl.formatMessage({ id: 'pages.orders.warehouseReceipt.receiptRegistered' }),
        );
        resetForm();
        onSuccess?.();
        onClose();
      } else {
        message.error(
          response?.message ||
            intl.formatMessage({ id: 'pages.orders.warehouseReceipt.receiptRegisterFailed' }),
        );
      }
    } catch (error: any) {
      // console.error('Error registering receipt:', error);
      message.error(
        error?.response?.data?.message ||
          intl.formatMessage({ id: 'pages.orders.warehouseReceipt.receiptRegisterFailed' }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div
          style={{
            background: 'linear-gradient(90deg, #1890ff 0%, #40a9ff 100%)',
            color: 'white',
            padding: '10px 16px',
            margin: '-20px -24px 20px -24px',
            fontSize: 18,
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {!scanData ? (
            <CloseOutlined
              onClick={onClose}
              style={{ cursor: 'pointer', fontSize: 18 }}
            />
          ) : (
            <span style={{ width: 24 }} />
          )}
          <span style={{ flex: 1, textAlign: 'center' }}>
            {intl.formatMessage({ id: 'pages.orders.user.warehouseScan' })}
          </span>
          <Select
            value={warehouse}
            onChange={setWarehouse}
            style={{ width: 150 }}
            size="small"
            loading={loadingWarehouses}
            placeholder={intl.formatMessage({
              id: 'pages.orders.warehouseReceipt.selectWarehousePlaceholder',
            })}
          >
            {warehouses.map((wh) => (
              <Option key={wh._id} value={wh._id}>
                {wh.name}
              </Option>
            ))}
          </Select>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      closeIcon={null}
      styles={{
        header: { padding: 0, border: 'none', marginBottom: 0 },
        body: { padding: '24px' },
      }}
    >
      <Spin spinning={loading || submitting}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Upload Choice Modal */}
          <Modal
            title={intl.formatMessage({
              id: 'pages.orders.warehouseReceipt.chooseUploadMethod',
            })}
            open={showUploadChoice}
            onCancel={() => {
              setShowUploadChoice(false);
              setUploadChoiceType(null);
            }}
            footer={null}
            width={400}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Button
                type="primary"
                icon={<CameraOutlined />}
                size="large"
                block
                onClick={handleChoiceCamera}
              >
                {intl.formatMessage({
                  id: 'pages.orders.warehouseReceipt.takePhotoWithCamera',
                })}
              </Button>
              <Button
                icon={<UploadOutlined />}
                size="large"
                block
                onClick={handleChoiceFile}
              >
                {intl.formatMessage({
                  id: 'pages.orders.warehouseReceipt.uploadFromFiles',
                })}
              </Button>
            </Space>
          </Modal>

          {/* Camera View */}
          {showCamera && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#000',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{
                  facingMode: 'environment',
                  width: { ideal: 1280 },
                  height: { ideal: 720 },
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 40,
                  display: 'flex',
                  gap: 16,
                }}
              >
                <Button
                  danger
                  size="large"
                  onClick={() => {
                    setShowCamera(false);
                    setCameraType(null);
                  }}
                >
                  {intl.formatMessage({ id: 'pages.common.cancel' })}
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleCapturePhoto}
                  style={{
                    backgroundColor: '#52c41a',
                    borderColor: '#52c41a',
                  }}
                >
                  {intl.formatMessage({ id: 'pages.orders.warehouseReceipt.capture' })}
                </Button>
              </div>
            </div>
          )}

          {/* Tracking Number Input */}
          {!showImageUpload && !showRackDesignation && (
            <>
              <div>
                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                  {intl.formatMessage({
                    id: 'pages.orders.warehouseReceipt.trackingNumber',
                  })}
                </div>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    onPressEnter={handleScan}
                    size="large"
                    placeholder={intl.formatMessage({
                      id: 'pages.orders.warehouseReceipt.trackingNumberPlaceholder',
                    })}
                    disabled={!!scanData}
                  />
                  <Button
                    type="primary"
                    size="large"
                    icon={<ScanOutlined />}
                    onClick={handleScan}
                    loading={loading}
                    disabled={!!scanData}
                  >
                    {intl.formatMessage({ id: 'pages.orders.warehouseReceipt.scan' })}
                  </Button>
                </Space.Compact>
              </div>

              {/* Scan Results */}
              {scanData && (
                <Card>
                  {/* No order found */}
                  {!scanData.order && (
                    <Alert
                      message={intl.formatMessage({
                        id: 'pages.orders.warehouseReceipt.noMatchedOrder',
                      })}
                      description={scanData.message}
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {/* Order found */}
                  {scanData.order && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                          {intl.formatMessage({
                            id: 'pages.orders.warehouseReceipt.orderNumber',
                          })}{' '}
                          {scanData.order.orderNumber || scanData.order.parentOrderNo}
                        </div>
                        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>
                          {intl.formatMessage({
                            id: 'pages.orders.warehouseReceipt.products',
                          })}
                        </div>
                        {scanData.order.items.map((item, idx) => (
                          <Card
                            key={idx}
                            size="small"
                            style={{ marginBottom: 8 }}
                          >
                            <div style={{ display: 'flex', gap: 12 }}>
                              <Image
                                src={item.imageUrl}
                                alt={item.subject}
                                width={80}
                                height={80}
                                style={{ objectFit: 'cover', borderRadius: 4 }}
                                preview={{ mask: false, zIndex: 10000 }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                  {item.subject}
                                </div>
                                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                                  {intl.formatMessage({
                                    id: 'pages.orders.warehouseReceipt.quantity',
                                  })}
                                  : {item.quantity}
                                </div>
                                {item.skuAttributes.map((attr, attrIdx) => (
                                  <div key={attrIdx} style={{ fontSize: 12, color: '#666' }}>
                                    {attr.attributeName}: {attr.value}
                                  </div>
                                ))}

                                {/* Notes */}
                                {(item.notes || item.notesZh) && (
                                  <div style={{ marginTop: 6, padding: '4px 8px', background: '#fffbe6', borderRadius: 4, border: '1px solid #ffe58f' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 11, color: '#d48806', marginBottom: 2 }}>
                                      {intl.formatMessage({ id: 'pages.orders.warehouseReceipt.notes' })}
                                    </div>
                                    {item.notes && (
                                      <div style={{ fontSize: 12, color: '#333' }}>
                                        {item.notes}
                                      </div>
                                    )}
                                    {item.notesZh && (
                                      <div style={{ fontSize: 12, color: '#333' }}>
                                        <span style={{ color: '#999', marginRight: 4 }}>ZH:</span>{item.notesZh}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Designated Shooting */}
                                {item.designatedShooting && item.designatedShooting.length > 0 && (
                                  <div style={{ marginTop: 6 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 11, color: '#1d39c4', marginBottom: 4 }}>
                                      {intl.formatMessage({ id: 'pages.orders.warehouseReceipt.designatedShooting' })}
                                    </div>
                                    <Table
                                      dataSource={item.designatedShooting.map((ds, i) => ({ ...ds, _key: i }))}
                                      rowKey="_key"
                                      size="small"
                                      pagination={false}
                                      bordered
                                      columns={[
                                        {
                                          title: '#',
                                          dataIndex: '_key',
                                          width: 32,
                                          render: (v: number) => v + 1,
                                        },
                                        {
                                          title: intl.formatMessage({ id: 'pages.orders.warehouseReceipt.designatedShootingPhoto' }),
                                          dataIndex: 'photo',
                                          width: 70,
                                          render: (url: string) => url ? (
                                            <Image src={url} width={50} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} preview={{ mask: false, zIndex: 10000 }} />
                                          ) : '-',
                                        },
                                        {
                                          title: 'Note (ZH)',
                                          dataIndex: 'noteZh',
                                          render: (v: string) => v || '-',
                                        },
                                        {
                                          title: 'Note (KO)',
                                          dataIndex: 'note',
                                          render: (v: string, row: any) =>  v || '-',
                                        },
                                      ]}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                  )}

                  {/* Action Buttons */}
                  <Space style={{ width: '100%' }}>
                    <Button onClick={handleCancelAction}>
                      {intl.formatMessage({ id: 'pages.common.cancel' })}
                    </Button>
                    {!scanData.order && (
                      <Button type="primary" onClick={handleRegisterNoData}>
                        {intl.formatMessage({
                          id: 'pages.orders.warehouseReceipt.registerNoData',
                        })}
                      </Button>
                    )}
                    {scanData.order && !scanData.exists && (
                      <Button type="primary" onClick={handleRegister}>
                        {intl.formatMessage({ id: 'pages.orders.warehouseReceipt.register' })}
                      </Button>
                    )}
                    {scanData.exists && (
                      <>
                        <Button type="primary" onClick={handleReinspection}>
                          {intl.formatMessage({
                            id: 'pages.orders.warehouseReceipt.reinspection',
                          })}
                        </Button>
                        <Button onClick={handleRackDesignation}>
                          {intl.formatMessage({
                            id: 'pages.orders.warehouseReceipt.rackDesignation',
                          })}
                        </Button>
                      </>
                    )}
                  </Space>
                </Card>
              )}
            </>
          )}

          {/* Rack Designation Section */}
          {showRackDesignation && scanData && (
            <>
              <Alert
                message={intl.formatMessage({
                  id: 'pages.orders.warehouseReceipt.rackDesignationTitle',
                })}
                description={intl.formatMessage(
                  { id: 'pages.orders.warehouseReceipt.rackDesignationTracking' },
                  { trackingNumber },
                )}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <div>
                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                  {intl.formatMessage({
                    id: 'pages.orders.warehouseReceipt.locationCode',
                  })}
                </div>
                <Input
                  value={locationCode}
                  onChange={(e) => setLocationCode(e.target.value)}
                  onPressEnter={handleRackDesignationConfirm}
                  size="large"
                  placeholder={intl.formatMessage({
                    id: 'pages.orders.warehouseReceipt.locationCodePlaceholder',
                  })}
                />
              </div>

              <Space style={{ width: '100%' }}>
                <Button onClick={handleCancelRackDesignation}>
                  {intl.formatMessage({ id: 'pages.common.cancel' })}
                </Button>
                <Button
                  type="primary"
                  onClick={handleRackDesignationConfirm}
                  loading={submitting}
                >
                  {intl.formatMessage({ id: 'pages.common.confirm' })}
                </Button>
              </Space>
            </>
          )}

          {/* Image Upload Section */}
          {showImageUpload && scanData && (
            <>
              {/* Waybill Images */}
              <div>
                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                  {intl.formatMessage({
                    id: 'pages.orders.warehouseReceipt.waybillImagesRequired',
                  })}
                </div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => handleShowUploadChoice('waybill')}
                    disabled={waybillImages.length >= 10}
                    block
                  >
                    {intl.formatMessage({
                      id: 'pages.orders.warehouseReceipt.uploadWaybillImage',
                    })}
                  </Button>
                  {waybillImages.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {waybillImages.map((file) => (
                        <div
                          key={file.uid}
                          style={{
                            position: 'relative',
                            width: 104,
                            height: 104,
                            border: '1px solid #d9d9d9',
                            borderRadius: 8,
                            overflow: 'hidden',
                          }}
                        >
                          <Image
                            src={file.url || file.thumbUrl}
                            alt={file.name}
                            width={104}
                            height={104}
                            style={{ objectFit: 'cover' }}
                            preview={{ zIndex: 10000 }}
                          />
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                              setWaybillImages(waybillImages.filter((f) => f.uid !== file.uid));
                            }}
                            style={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </Space>
              </div>

              {/* Has Issue Switch (only if not "no data" case) */}
              {scanData.order && (
                <div>
                  <Space>
                    <span style={{ fontWeight: 'bold' }}>
                      {intl.formatMessage({
                        id: 'pages.orders.warehouseReceipt.hasIssue',
                      })}
                      :
                    </span>
                    <Switch checked={hasIssue} onChange={setHasIssue} />
                  </Space>
                </div>
              )}

              {/* Product or Issue Images */}
              {scanData.order && (
                <div>
                  <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                    {hasIssue
                      ? intl.formatMessage({
                          id: 'pages.orders.warehouseReceipt.issueImages',
                        })
                      : intl.formatMessage({
                          id: 'pages.orders.warehouseReceipt.productImages',
                        })}{' '}
                    (Max 10)
                  </div>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Button
                      icon={<UploadOutlined />}
                      onClick={() =>
                        handleShowUploadChoice(hasIssue ? 'issue' : 'product')
                      }
                      disabled={
                        (hasIssue ? issueImages.length : productImages.length) >= 10
                      }
                      block
                    >
                      {hasIssue
                        ? intl.formatMessage({
                            id: 'pages.orders.warehouseReceipt.uploadIssueImage',
                          })
                        : intl.formatMessage({
                            id: 'pages.orders.warehouseReceipt.uploadProductImage',
                          })}
                    </Button>
                    {(hasIssue ? issueImages : productImages).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {(hasIssue ? issueImages : productImages).map((file) => (
                          <div
                            key={file.uid}
                            style={{
                              position: 'relative',
                              width: 104,
                              height: 104,
                              border: '1px solid #d9d9d9',
                              borderRadius: 8,
                              overflow: 'hidden',
                            }}
                          >
                            <Image
                              src={file.url || file.thumbUrl}
                              alt={file.name}
                              width={104}
                              height={104}
                              style={{ objectFit: 'cover' }}
                              preview={{ zIndex: 10000 }}
                            />
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<CloseOutlined />}
                              onClick={() => {
                                if (hasIssue) {
                                  setIssueImages(issueImages.filter((f) => f.uid !== file.uid));
                                } else {
                                  setProductImages(productImages.filter((f) => f.uid !== file.uid));
                                }
                              }}
                              style={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </Space>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                  {intl.formatMessage({ id: 'pages.orders.warehouseReceipt.adminMemo' })}
                </div>
                <TextArea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={intl.formatMessage({
                    id: 'pages.orders.warehouseReceipt.adminMemoPlaceholder',
                  })}
                />
              </div>

              {/* Cash on Delivery (Optional) - value in CNY */}
              {/* <div>
                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                  {intl.formatMessage({
                    id: 'pages.orders.warehouseReceipt.cashOnDeliveryOptional',
                  })}{' '}
                  <span style={{ fontWeight: 'normal', color: '#666' }}>(CNY)</span>
                </div>
                <InputNumber
                  value={cashOnDelivery}
                  onChange={(value) => setCashOnDelivery(value ?? 0)}
                  size="large"
                  placeholder={intl.formatMessage({
                    id: 'pages.orders.warehouseReceipt.cashOnDeliveryPlaceholder',
                  })}
                  addonBefore="¥"
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  formatter={(value) =>
                    typeof value === 'number' || (typeof value === 'string' && value !== '')
                      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      : ''
                  }
                  parser={(value) =>
                    value ? Number(value.replace(/\$\s?|(,*)/g, '')) || 0 : 0
                  }
                />
              </div> */}

              {/* Confirm Button */}
              <Button
                type="primary"
                size="large"
                block
                onClick={handleConfirm}
                loading={submitting}
              >
                {intl.formatMessage({ id: 'pages.common.confirm' })}
              </Button>
            </>
          )}
        </Space>
      </Spin>
    </Modal>
  );
};

export default WarehouseReceiptModal;
